import * as React from 'react'
import {
  AlertTriangle,
  Check,
  ChevronRight,
  Info,
  Loader2,
  MapPin,
} from 'lucide-react'
import type { HcpJobLineDto, HcpJobPreviewDto } from '@lib/dto/hcp-job.dto'
import { HCP_JOB_KIND_LABELS } from '@lib/enums/hcp-job-kind.enum'
import {
  hcpWorkStatusWords,
  isHcpWorkCanceled,
} from '@lib/enums/hcp-work-status.enum'
import { formatDate } from '@lib/helpers/format'
import { hcpLineCents } from '@lib/helpers/hcp-jobs'
import { formatMoney, parseMoney } from '@lib/helpers/money'
import { settleHcpJobInputSchema } from '@lib/input/housecall-pro.input'
import { useHousecallProStatus } from '@/hooks/useHousecallPro'
import {
  useCreateHcpJob,
  useProposalHcpJobs,
  useSettleHcpJob,
} from '@/hooks/useProposalHcpJobs'
import { errorMessage } from '@/helpers/logger'
import { StatusPill } from '@/components/StatusPill'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import {
  TABLE,
  TABLE_FRAME,
  TD,
  TD_RIGHT,
  TH,
  TH_RIGHT,
} from '@/components/ui/table-classes'
import { cn } from '@/lib/utils'

function money(value: string): string {
  return formatMoney(parseMoney(value) ?? 0)
}

/**
 * Where a created job stands, as HCP last said: done (and its repairs
 * closed), canceled, or its status as HCP words it ("Scheduled"), which is
 * also how a status this CRM does not know yet is shown.
 */
function progressWords(
  record: NonNullable<HcpJobPreviewDto['record']>,
  repairCount: number,
): string {
  if (record.completedAt !== null) {
    const closed =
      repairCount === 0
        ? ''
        : `; ${repairCount === 1 ? 'its repair' : `its ${String(repairCount)} repairs`} closed`
    return `Done ${formatDate(record.completedAt)}${closed}`
  }
  const status = record.workStatus
  if (status === null) return 'Not read from Housecall Pro yet'
  if (isHcpWorkCanceled(status)) return 'Canceled in Housecall Pro'
  const words = hcpWorkStatusWords(status)
  return words.charAt(0).toUpperCase() + words.slice(1)
}

interface ProposalHcpJobsProps {
  versionId: string
}

/**
 * What an accepted version becomes in Housecall Pro: one job per site for its
 * tree work and one for its repairs, line by line, with where each goes, what
 * has to be settled first, and the button that creates it. Shown only where
 * HCP is connected.
 */
export function ProposalHcpJobs({ versionId }: ProposalHcpJobsProps) {
  const status = useHousecallProStatus()
  const configured = status.data?.configured === true
  const jobs = useProposalHcpJobs(versionId, configured)

  if (!configured) return null

  function renderBody() {
    if (jobs.isPending) {
      return (
        <div className="flex justify-center py-4">
          <Loader2 className="h-4 w-4 animate-spin text-muted-text" />
        </div>
      )
    }
    if (jobs.isError) {
      return <p className="text-sm text-destructive">{errorMessage(jobs.error, 'Something went wrong. Please try again.')}</p>
    }
    const data = jobs.data
    if (data.unavailableReason !== null) {
      return <p className="text-sm text-muted-text">{data.unavailableReason}</p>
    }
    if (data.jobs.length === 0) {
      return (
        <p className="text-sm text-muted-text">
          Nothing was accepted that a crew would go out for.
        </p>
      )
    }
    return (
      <div className="space-y-2">
        {data.jobs.map((job, index) => (
          <JobCard
            key={`${job.siteName}:${job.kind}:${String(index)}`}
            job={job}
            versionId={versionId}
            customerName={data.customer?.name ?? null}
            writesEnabled={data.writesEnabled}
          />
        ))}
        <p className="text-xs text-muted-text">
          Prices are the ones the client accepted, before tax. Housecall Pro
          adds its own sales tax line to each job.
        </p>
      </div>
    )
  }

  return (
    <section className="space-y-2">
      <div>
        <h3 className="text-sm font-medium">Jobs in Housecall Pro</h3>
        <p className="text-xs text-muted-text">
          {jobs.data?.customer === null || jobs.data === undefined
            ? 'One job per site for its tree work, and one for its repairs.'
            : `One job per site for its tree work, and one for its repairs, on ${jobs.data.customer.name}.`}
        </p>
      </div>
      {renderBody()}
    </section>
  )
}

interface JobCardProps {
  job: HcpJobPreviewDto
  versionId: string
  customerName: string | null
  writesEnabled: boolean
}

/** One job: what and where, where it stands, and its lines on request. */
function JobCard({ job, versionId, customerName, writesEnabled }: JobCardProps) {
  const [open, setOpen] = React.useState<boolean>(false)
  const [confirming, setConfirming] = React.useState<boolean>(false)
  const linesId = React.useId()
  const title = `${job.siteName} · ${HCP_JOB_KIND_LABELS[job.kind]}`

  function renderStanding() {
    const { record } = job
    if (record === null) {
      if (job.blockers.length > 0) return null
      if (!writesEnabled) {
        return (
          <p className="text-xs text-muted-text">
            Creating jobs in Housecall Pro is switched off in Settings.
          </p>
        )
      }
      return (
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setConfirming(true)
          }}
        >
          Create in Housecall Pro
        </Button>
      )
    }
    if (record.state === 'CREATED') {
      return (
        <div className="space-y-0.5">
          <p className="flex flex-wrap items-center gap-1.5 text-xs text-muted-text">
            <StatusPill tone={record.completedAt === null ? 'blue' : 'green'}>
              <Check className="mr-1 h-3 w-3" />
              {record.invoiceNumber === null ? 'In Housecall Pro' : `Job ${record.invoiceNumber}`}
            </StatusPill>
            <span className="text-secondary-text">
              {progressWords(record, job.repairKeys.length)}
            </span>
          </p>
          <p className="text-xs text-muted-text">
            {`Created ${formatDate(record.createdAt)}${record.createdByName === null ? '' : ` by ${record.createdByName}`}`}
          </p>
        </div>
      )
    }
    if (record.state === 'CREATING') {
      return (
        <p className="flex items-center gap-1.5 text-xs text-muted-text">
          <Loader2 className="h-3 w-3 animate-spin" />
          Being created in Housecall Pro…
        </p>
      )
    }
    return <SettleForm versionId={versionId} recordId={record.id} />
  }

  return (
    <div className="rounded-md border border-default bg-surface">
      <div className="flex flex-wrap items-start justify-between gap-2 px-3 py-2">
        <div className="min-w-0 space-y-0.5">
          <p className="text-sm font-medium">{title}</p>
          <p className="flex items-center gap-1 text-xs text-secondary-text">
            <MapPin className="h-3 w-3 shrink-0" />
            {job.address === null ? (
              <span className="italic text-muted-text">No address in Housecall Pro yet</span>
            ) : (
              <span className="truncate">{job.address.label}</span>
            )}
          </p>
        </div>
        <div className="text-right">
          <p className="text-sm font-medium tabular-nums">{money(job.amount)}</p>
          <button
            type="button"
            className="inline-flex items-center gap-0.5 text-xs text-secondary-text hover:text-primary-text"
            aria-expanded={open}
            aria-controls={linesId}
            onClick={() => {
              setOpen(!open)
            }}
          >
            <ChevronRight
              className={cn('h-3 w-3 transition-transform', open ? 'rotate-90' : null)}
            />
            {job.lines.length} {job.lines.length === 1 ? 'line' : 'lines'}
          </button>
        </div>
      </div>

      {job.blockers.length === 0 && job.warnings.length === 0 ? null : (
        <ul className="space-y-1 border-t border-default px-3 py-2">
          {job.blockers.map((blocker) => (
            <li key={blocker} className="flex gap-1.5 text-xs text-secondary-text">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" />
              {blocker}
            </li>
          ))}
          {job.warnings.map((warning) => (
            <li key={warning} className="flex gap-1.5 text-xs text-muted-text">
              <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              {warning}
            </li>
          ))}
        </ul>
      )}

      {open ? (
        <div id={linesId} className="border-t border-default p-2">
          <LinesTable lines={job.lines} />
        </div>
      ) : null}

      <div className="border-t border-default px-3 py-2 empty:hidden">{renderStanding()}</div>

      {confirming ? (
        <CreateJobDialog
          job={job}
          versionId={versionId}
          customerName={customerName}
          onClose={() => {
            setConfirming(false)
          }}
        />
      ) : null}
    </div>
  )
}

/** A job's lines as HCP will hold them, each rounded as HCP rounds a line. */
function LinesTable({ lines }: { lines: readonly HcpJobLineDto[] }) {
  return (
    <div className={TABLE_FRAME}>
      <table className={TABLE}>
        <thead>
          <tr className="border-b border-default text-secondary-text">
            <th className={TH}>Line</th>
            <th className={TH_RIGHT}>Qty</th>
            <th className={TH_RIGHT}>Price</th>
            <th className={TH_RIGHT}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line, index) => (
            <tr
              // Lines have no id of their own until HCP gives them one.
              key={`${line.name}:${String(index)}`}
              className="border-b border-default last:border-b-0"
            >
              <td className={cn(TD, 'whitespace-normal')}>
                <p className="font-medium">{line.name}</p>
                {line.description === '' ? null : (
                  <p className="text-xs whitespace-pre-line text-muted-text">
                    {line.description}
                  </p>
                )}
              </td>
              <td className={cn(TD_RIGHT, 'tabular-nums align-top')}>{line.quantity}</td>
              <td className={cn(TD_RIGHT, 'tabular-nums align-top')}>
                {money(line.unitPrice)}
              </td>
              <td className={cn(TD_RIGHT, 'tabular-nums align-top')}>
                {formatMoney(hcpLineCents(line))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

interface CreateJobDialogProps {
  job: HcpJobPreviewDto
  versionId: string
  customerName: string | null
  onClose: () => void
}

/**
 * The last look before a job lands in the live account: the customer, the
 * address, every line and the total. The lines are the ones the API will
 * send, because it sends what this page was shown.
 */
function CreateJobDialog({ job, versionId, customerName, onClose }: CreateJobDialogProps) {
  const create = useCreateHcpJob(versionId)
  const [error, setError] = React.useState<string | null>(null)

  async function handleCreate(): Promise<void> {
    if (job.siteId === null) return
    setError(null)
    try {
      await create.mutateAsync({ siteId: job.siteId, kind: job.kind })
      onClose()
    } catch (caught) {
      setError(errorMessage(caught, 'Something went wrong. Please try again.'))
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !create.isPending) onClose()
      }}
    >
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Create the {HCP_JOB_KIND_LABELS[job.kind].toLowerCase()} for {job.siteName} in
            Housecall Pro?
          </DialogTitle>
          <DialogDescription>
            On {customerName ?? 'the client’s customer'}, at {job.address?.label ?? 'its address'}.
            It goes into the live account unscheduled, where dispatch will see
            it, and cannot be undone from here.
          </DialogDescription>
        </DialogHeader>
        <LinesTable lines={job.lines} />
        <p className="text-right text-sm">
          <span className="text-muted-text">Before tax </span>
          <span className="font-medium tabular-nums">{money(job.amount)}</span>
        </p>
        {error === null ? null : <p className="text-sm text-destructive">{error}</p>}
        <DialogFooter>
          <Button size="sm" variant="outline" disabled={create.isPending} onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            disabled={create.isPending}
            onClick={() => {
              void handleCreate()
            }}
          >
            {create.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
            Create job
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * A job HCP did not confirm. Nothing more can be created for it until a
 * person has looked in HCP: either it is there (its id is recorded, after
 * HCP confirms it is this client's job), or it is not (and it can be created
 * again).
 */
function SettleForm({ versionId, recordId }: { versionId: string; recordId: string }) {
  const settle = useSettleHcpJob(versionId)
  const [jobId, setJobId] = React.useState<string>('')
  const [error, setError] = React.useState<string | null>(null)

  async function handleSettle(hcpJobId: string | null): Promise<void> {
    setError(null)
    const parsed = settleHcpJobInputSchema.safeParse({ hcpJobId })
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Check the job id')
      return
    }
    try {
      await settle.mutateAsync({ recordId, hcpJobId: parsed.data.hcpJobId })
    } catch (caught) {
      setError(errorMessage(caught, 'Something went wrong. Please try again.'))
    }
  }

  return (
    <div className="space-y-2">
      <p className="flex gap-1.5 text-xs text-secondary-text">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" />
        Housecall Pro did not confirm this job, so it may or may not be there.
        Look for it on the customer in Housecall Pro before anything else.
      </p>
      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(event) => {
          event.preventDefault()
          void handleSettle(jobId.trim())
        }}
      >
        <Input
          aria-label="Housecall Pro job id"
          className="h-7 w-56 text-xs"
          placeholder="job_… from its address there"
          value={jobId}
          onChange={(event) => {
            setJobId(event.target.value)
          }}
        />
        <Button type="submit" size="sm" variant="outline" disabled={settle.isPending}>
          It’s there: record it
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={settle.isPending}
          onClick={() => {
            void handleSettle(null)
          }}
        >
          It isn’t there
        </Button>
      </form>
      {error === null ? null : <p className="text-xs text-destructive">{error}</p>}
    </div>
  )
}
